import React from "react";
import { Box, Grid, Stack, useMediaQuery } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useOTBStyle from "../OTBStyle";
import VSNTypography from "../../../shared/VSNTypography";

const MRF = ({ data, pdf }) => {
  const classes = useOTBStyle();
  const isSm = useMediaQuery("(max-width: 600px)");

  const groupBy = (key) =>
    data?.reduce((acc, item) => {
      const value = item[key];
      if (
        value !== null &&
        value !== undefined &&
        (!Array.isArray(value) || value?.length > 0)
      ) {
        acc[value] = acc[value] || [];
        acc[value].push(item);
      }

      return acc;
    }, {});

  const MRFData = Object?.entries(groupBy("referrals") || {}).map(
    ([key, items]) => ({
      name: key,
      value: items.length,
    })
  );

  const controlQuestionnaireData = Object?.entries(groupBy("mrfs") || {})?.map(
    ([key, items]) => ({
      name: key === "1" ? "365 Days Cleared" : "Not Cleared",
      value: items.length,
    })
  );

  const totalReferrals = MRFData?.reduce((acc, item) => acc + item.value, 0);

  const option = {
    tooltip: {
      trigger: "none",
    },

    series: [
      {
        type: "tree",
        data: [
          {
            name: "Referrals",
            value: totalReferrals,
            itemStyle: {
              color: "#FAF3EA",
            },
            label: {
              position: "inside",
              color: "#333",
              fontSize: 24,
              backgroundColor: "#FAF3EA",
              borderRadius: 8,
              padding: [20, 20, 20, 20],
              rich: {
                a: {
                  fontSize: 26,
                  color: "#333",
                  align: "middle",
                  borderRadius: 8,
                  fontWeight: 500,
                },
              },
              formatter: (params) => {
                return `{a|${params.name}\n${params.value}}`;
              },
            },
            children: MRFData?.map((category) => ({
              name: category.name,
              value: category.value,
              itemStyle: {
                color: "#B2926380",
              },
              label: {
                position: "inside",
                color: "#B29263",
                fontSize: 18,
                padding: [0, 0, 0, 10],
                formatter: (params) => {
                  return `${params.value} ${params.name} `;
                },
              },
            })),
          },
        ],
        top: "8%",
        left: "10%",
        bottom: "8%",
        right: "35%",
        symbolSize: 10,
        edgeShape: "polyline",
        edgeForkPosition: "80%",
        initialTreeDepth: 6,
        lineStyle: {
          width: 3,
          color: "#B29263",
        },
        label: {
          position: "left",
          verticalAlign: "middle",
          align: "start",
          fontSize: 16,
        },
        emphasis: {
          focus: "none",
        },
        expandAndCollapse: false,
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
      },
    ],
  };

  return (
    <Box>
      {pdf && (
        <Box
          className={classes.pdfReportLayout}
          sx={{
            pageBreakAfter: "always",
            marginTop: "15px",
          }}
        >
          <VSNTypography variant="h4">
            {`Medical Recommendation  ${" "} Forms (MRF)`}
          </VSNTypography>
          <Stack gap={2}>
            <Stack>
              <Stack direction="row" gap={2} mb={2}>
                <Box className={classes.pdfReportCard}>
                  <VSNTypography variant="h2">Provider Visit</VSNTypography>

                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "4px !important" }}
                  >
                    {
                      data?.filter((patient) => patient?.provider_visit === 1)
                        .length
                    }
                    <span>
                      / {data?.length}
                      <span
                        style={{
                          color:
                            data?.filter(
                              (patient) => patient?.provider_visit === 1
                            ).length -
                              data?.length <
                            0
                              ? "red"
                              : "green",
                        }}
                      >
                        {" "}
                        (
                        {data?.filter(
                          (patient) => patient?.provider_visit === 1
                        ).length - data?.length}
                        )
                      </span>
                    </span>
                  </VSNTypography>
                </Box>

                <Box className={classes.pdfReportCard}>
                  <VSNTypography variant="h2">MRFs</VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "4px !important" }}
                  >
                    {data?.filter((patient) => patient?.mrfs === 1).length}
                    <span>
                      / {data?.length}
                      <span
                        style={{
                          color:
                            data?.filter((patient) => patient?.mrfs === 1)
                              .length -
                              data?.length <
                            0
                              ? "red"
                              : "green",
                        }}
                      >
                        {" "}
                        (
                        {data?.filter((patient) => patient?.mrfs === 1).length -
                          data?.length}
                        )
                      </span>
                    </span>
                  </VSNTypography>
                </Box>

                <Box className={classes.pdfReportCard}>
                  <VSNTypography variant="h2">MRF Reasons</VSNTypography>
                  <Stack>
                    <VSNTypography
                      className="record"
                      sx={{ marginTop: "4px !important" }}
                    >
                      Fitness
                    </VSNTypography>

                    <VSNTypography className="record">
                      {data?.reduce(
                        (sum, patient) => sum + (patient?.mrf_reasons || 0),
                        0
                      ) || 0}
                    </VSNTypography>
                  </Stack>
                </Box>
              </Stack>
            </Stack>

            <Stack direction="row">
              <ReactECharts
                option={option}
                style={{ height: "500px", width: "700px" }}
              />

              <ReactECharts
                option={{
                  title: { text: "Type of MRF Issued", left: "center" },
                  tooltip: { trigger: "item" },
                  legend: {
                    bottom: "0",
                    left: "center",
                    textStyle: {
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#000",
                    },
                  },
                  color: [
                    "#C3A577",
                    "#E0BE94",
                    "#E8D0B1",
                    "#F1E1CD",
                    "#B3B3B3",
                    "#B3B3B3",
                    "#808080",
                  ],
                  series: [
                    {
                      name: "MRF's",
                      type: "pie",
                      radius: "80%",
                      data: controlQuestionnaireData,
                      label: {
                        show: false,
                      },
                      animation: false,
                      animationDuration: 0,
                      animationDurationUpdate: 0,
                    },
                  ],
                }}
                style={{ height: "350px", width: "400px" }}
              />
            </Stack>
          </Stack>
        </Box>
      )}

      {!pdf && (
        <Box>
          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Medical Recommendation Forms (MRF)
              </VSNTypography>
            </div>
            <Grid container alignItems="center">
              <Grid item xs={12} smd={6}>
                <Grid container justifyContent="center">
                  <Grid item xs={12} xxl={10}>
                    <Grid container columnSpacing={1.2}>
                      <Grid item xs={12} lg={6}>
                        <Box className={classes.reportCard}>
                          <VSNTypography className={classes.reportCardHeading}>
                            Provider Visit
                          </VSNTypography>
                          <VSNTypography className="record">
                            {
                              data?.filter(
                                (patient) => patient?.provider_visit === 1
                              ).length
                            }
                            <span>
                              / {data?.length}
                              <span
                                style={{
                                  color:
                                    data?.filter(
                                      (patient) => patient?.provider_visit === 1
                                    ).length -
                                      data?.length <
                                    0
                                      ? "red"
                                      : "green",
                                }}
                              >
                                {" "}
                                (
                                {data?.filter(
                                  (patient) => patient?.provider_visit === 1
                                ).length - data?.length}
                                )
                              </span>
                            </span>
                          </VSNTypography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        <Box className={classes.reportCard}>
                          <VSNTypography className={classes.reportCardHeading}>
                            MRFs
                          </VSNTypography>
                          <VSNTypography className="record">
                            {
                              data?.filter((patient) => patient?.mrfs === 1)
                                .length
                            }
                            <span>
                              / {data?.length}
                              <span
                                style={{
                                  color:
                                    data?.filter(
                                      (patient) => patient?.mrfs === 1
                                    ).length -
                                      data?.length <
                                    0
                                      ? "red"
                                      : "green",
                                }}
                              >
                                {" "}
                                (
                                {data?.filter((patient) => patient?.mrfs === 1)
                                  .length - data?.length}
                                )
                              </span>
                            </span>
                          </VSNTypography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box className={classes.reportCard}>
                          <VSNTypography className={classes.reportCardHeading}>
                            MRF Reasons
                          </VSNTypography>

                          <Stack direction="row" justifyContent="space-between">
                            <VSNTypography className="record">
                              Fitness
                            </VSNTypography>

                            <VSNTypography className="record">
                              {data?.reduce(
                                (sum, patient) =>
                                  sum + (patient?.mrf_reasons || 0),
                                0
                              ) || 0}
                            </VSNTypography>
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} smd={6}>
                <Box sx={{ height: { lg: 550, md: 450, xs: 350 } }}>
                  <ReactECharts
                    option={{
                      title: { text: "Type of MRF Issued", left: "center" },
                      tooltip: { trigger: "item" },
                      legend: {
                        bottom: "5%",
                        textStyle: {
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#000",
                        },
                      },
                      color: [
                        "#C3A577",
                        "#E0BE94",
                        "#E8D0B1",
                        "#F1E1CD",
                        "#B3B3B3",
                        "#B3B3B3",
                        "#808080",
                      ],
                      series: [
                        {
                          name: "MRF's",
                          type: "pie",
                          radius: "50%",
                          data: controlQuestionnaireData,
                          animation: false,
                          animationDuration: 0,
                          animationDurationUpdate: 0,
                        },
                      ],
                    }}
                    style={{ height: "100%", width: "100%" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Medical Recommendation Forms (MRF) Referrals
              </VSNTypography>
            </div>
            <Box sx={{ height: { lg: 550, md: 450, xs: 350 } }}>
              <ReactECharts
                option={option}
                style={{ height: "100%", width: "100%" }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MRF;
